import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { supabase } from '@/api/supabaseClient';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import { Trash2, Shield, Truck, Mail, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import InvitationsSection from './InvitationsSection';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  hr: 'HR',
  accounting: 'Accounting',
  dispatch: 'Dispatch',
};

const ROLE_COLORS = {
  super_admin: 'text-yellow-700 dark:text-yellow-400 bg-yellow-500/15 dark:bg-yellow-500/10 border-yellow-500/40 dark:border-yellow-500/30',
  hr: 'text-blue-700 dark:text-blue-400 bg-blue-500/15 dark:bg-blue-500/10 border-blue-500/40 dark:border-blue-500/30',
  accounting: 'text-green-700 dark:text-green-400 bg-green-500/15 dark:bg-green-500/10 border-green-500/40 dark:border-green-500/30',
  dispatch: 'text-purple-700 dark:text-purple-400 bg-purple-500/15 dark:bg-purple-500/10 border-purple-500/40 dark:border-purple-500/30',
};

const DRIVER_TYPE_LABELS = {
  'Owner Operator': 'Owner Operator',
  'Company Driver': 'Company Driver',
};

const DRIVER_TYPE_COLORS = {
  'Owner Operator': 'text-blue-700 dark:text-blue-400 bg-blue-500/15 dark:bg-blue-500/10 border-blue-500/40 dark:border-blue-500/30',
  'Company Driver': 'text-teal-700 dark:text-teal-400 bg-teal-500/15 dark:bg-teal-500/10 border-teal-500/40 dark:border-teal-500/30',
};

export default function UserManagementTab() {
  const { staffRole } = useAuth();
  const canEditEmployeeNumber = staffRole === 'hr' || staffRole === 'super_admin';
  const [staffProfiles, setStaffProfiles] = useState([]);
  const [driverProfiles, setDriverProfiles] = useState([]);
  const [ownerUserId, setOwnerUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('staff');

  const withTimeout = (promise, ms = 8000) =>
    Promise.race([
      promise,
      new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
    ]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [staff, drivers, ownerMembership] = await Promise.all([
        withTimeout(client.entities.StaffProfile.list('-created_date', 100)).catch(() => []),
        withTimeout(client.entities.DriverProfile.list('-created_date', 200)).catch(() => []),
        // Whichever carrier is RLS-scoped for the current session -- this is
        // NOT specific to any one person's email (that was the old bug: a
        // single hardcoded "Marcus G" row shown on every carrier's Staff
        // page regardless of who actually owns it).
        supabase.from('memberships').select('user_id').eq('role', 'owner').eq('status', 'active').limit(1).maybeSingle()
          .then(({ data }) => data?.user_id || null).catch(() => null),
      ]);
      setStaffProfiles(staff);
      setDriverProfiles(drivers);
      setOwnerUserId(ownerMembership);
    } catch (e) {
      // ignore — individual .catch(() => []) handles each failure
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const handleDeactivate = async (profile) => {
    if (!window.confirm(`Deactivate ${profile.full_name || profile.email}? They will lose access immediately.`)) return;
    await client.entities.StaffProfile.update(profile.id, { is_active: false });
    toast.success('Staff member deactivated.');
    loadAll();
  };

  const handleReactivate = async (profile) => {
    await client.entities.StaffProfile.update(profile.id, { is_active: true });
    toast.success('Staff member reactivated.');
    loadAll();
  };

  const handleEditEmployeeNumber = async (profile) => {
    const value = window.prompt(`Employee # for ${profile.full_name || profile.user_email}:`, profile.employee_number || '');
    if (value === null) return; // cancelled
    await client.entities.StaffProfile.update(profile.id, { employee_number: value.trim() });
    toast.success('Employee # updated.');
    loadAll();
  };

  const handleDeleteStaff = async (profile) => {
    if (!window.confirm(`Permanently remove ${profile.full_name || profile.email}? This revokes their access to this carrier entirely -- they can be invited again fresh later if needed.`)) return;
    try {
      // Removes BOTH the staff_profiles row (admin-panel access mirror)
      // AND the underlying membership (the real tenant-access record) --
      // deleting staff_profiles alone left a "deleted" person still an
      // active member, invisibly blocking any future re-invite to their
      // email and misrouting them to /onboarding if they ever signed in.
      await client.rpc('remove_member', { p_user_id: profile.user_id });
      toast.success('Staff member removed.');
      loadAll();
    } catch (e) {
      toast.error(e?.message || 'Could not remove staff member.');
    }
  };

  // Same "real driver" definition as DriverManagement.jsx's 'All' tab --
  // an unreviewed applicant (approval_status: Pending) isn't a driver yet,
  // they're an applicant that belongs in Approvals. Without this filter,
  // this card's counts disagreed with DriverManagement.jsx's own list for
  // the exact same underlying data (e.g. a driver mid-review counted here
  // as one of "N Company Drivers" while being invisible in the Driver
  // Management list that's supposed to be the source of truth for it).
  const reviewedDrivers = driverProfiles.filter(d => d.approval_status !== 'Pending');
  const ownerOperators = reviewedDrivers.filter(d => d.driver_type === 'Owner Operator' || !d.driver_type);
  const companyDrivers = reviewedDrivers.filter(d => d.driver_type === 'Company Driver');

  // Defensive: a staff_profiles row with no user_id was never linked to a
  // real account -- e.g. leftover rows from the now-removed "Add Staff"
  // flow, which created a row directly with no invite/membership/account
  // behind it at all. Filtering these out of display (rather than just no
  // longer creating new ones) means any stray ones left over don't show
  // as confusing phantom "staff members" who can't actually be managed
  // (remove_member requires a real user_id and would just error on one).
  const realStaffProfiles = staffProfiles.filter(p => p.user_id);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Section toggle */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveSection('staff')}
          className={cn("flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors",
            activeSection === 'staff' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
          )}
        >
          <Shield className="w-4 h-4" /> Staff / Employees
        </button>
        <button
          onClick={() => setActiveSection('invitations')}
          className={cn("flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors",
            activeSection === 'invitations' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
          )}
        >
          <Mail className="w-4 h-4" /> Invitations
        </button>
        <button
          onClick={() => setActiveSection('drivers')}
          className={cn("flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors",
            activeSection === 'drivers' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
          )}
        >
          <Truck className="w-4 h-4" /> Driver Types
        </button>
      </div>

      {/* ── INVITATIONS SECTION ── */}
      {activeSection === 'invitations' && <InvitationsSection />}

      {/* ── STAFF SECTION ── */}
      {activeSection === 'staff' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-foreground">Staff Members</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Controls which Admin Panel sections each employee can access</p>
            </div>
            <button
              onClick={() => setActiveSection('invitations')}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <Mail className="w-4 h-4" /> Invite Staff
            </button>
          </div>

          <div className="glass-card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">Name / Email</th>
                  <th className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">Role</th>
                  <th className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">Access</th>
                  <th className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">Employee #</th>
                  <th className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {realStaffProfiles.map(p => {
                  // The carrier owner always has a mirrored staff_profiles row
                  // (staff_role='super_admin', migration 0016) so they show up
                  // here like anyone else -- just flagged as Owner and without
                  // deactivate/delete controls, since removing your own access
                  // to your own carrier isn't a thing this screen should allow.
                  const isOwner = !!ownerUserId && p.user_id === ownerUserId;
                  return (
                    <tr key={p.id} className={cn(isOwner && 'bg-yellow-500/5', !p.is_active && 'opacity-50')}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-foreground">{p.full_name || p.user_email || '—'}</p>
                        <p className="text-xs text-muted-foreground">{p.user_email || p.email || ''}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn("text-xs font-semibold px-2 py-0.5 rounded-full border", ROLE_COLORS[p.staff_role])}>
                          {ROLE_LABELS[p.staff_role] || p.staff_role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {p.staff_role === 'super_admin' && 'All tabs'}
                        {p.staff_role === 'hr' && 'HR only'}
                        {p.staff_role === 'accounting' && 'Payroll + Accounting'}
                        {p.staff_role === 'dispatch' && 'Dispatch only'}
                      </td>
                      <td className="px-4 py-3">
                        {canEditEmployeeNumber ? (
                          <button
                            onClick={() => handleEditEmployeeNumber(p)}
                            className="flex items-center gap-1.5 text-xs text-foreground hover:text-primary transition-colors"
                          >
                            {p.employee_number || <span className="text-muted-foreground italic">Set</span>}
                            <Pencil className="w-3 h-3 text-muted-foreground" />
                          </button>
                        ) : (
                          <span className="text-xs text-foreground">{p.employee_number || <span className="text-muted-foreground">—</span>}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn("text-xs font-semibold", p.is_active ? 'text-green-400' : 'text-red-400')}>
                          {p.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {isOwner ? (
                          <p className="text-xs text-muted-foreground italic text-right">Owner</p>
                        ) : (
                          <div className="flex items-center gap-2 justify-end">
                            {p.is_active ? (
                              <button onClick={() => handleDeactivate(p)} className="text-xs text-amber-400 hover:text-amber-300 transition-colors">
                                Deactivate
                              </button>
                            ) : (
                              <button onClick={() => handleReactivate(p)} className="text-xs text-green-400 hover:text-green-300 transition-colors">
                                Reactivate
                              </button>
                            )}
                            <button onClick={() => handleDeleteStaff(p)} className="p-1 text-muted-foreground hover:text-red-400 transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {realStaffProfiles.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-muted-foreground py-8 text-sm">
                      No staff members yet. Send an invite to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── DRIVER TYPES SECTION ── */}
      {activeSection === 'drivers' && (
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-foreground">Driver & Owner Classification</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Driver types are set on each driver profile during onboarding or by HR</p>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Owner Operators', count: ownerOperators.length, color: 'text-blue-400', bg: 'bg-blue-500/10', desc: 'Own truck + drive themselves' },
              { label: 'Company Drivers', count: companyDrivers.length, color: 'text-teal-400', bg: 'bg-teal-500/10', desc: 'Drive company-owned or owner truck' },
            ].map(s => (
              <div key={s.label} className="glass-card p-4">
                <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center mb-2", s.bg)}>
                  <Truck className={cn("w-4 h-4", s.color)} />
                </div>
                <p className={cn("text-2xl font-space font-bold", s.color)}>{s.count}</p>
                <p className="text-sm font-medium text-foreground">{s.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
